import Button from "@rio-cloud/rio-uikit/Button";
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, { TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import omit from "lodash/fp/omit";
import { useState } from "react";
import { CreateAreaReworkPayload, ReworkArea } from "../../types/areas";
import ChipStatus from "../ChipStatus";
import Paginator from "../Paginator";
import TableRowLoading from "../TableRowLoading";

const defaultColumnOrder = [
  "name",
  "use",
  "minimumVelocity",
  "maxVelocity",
  "minimumPermanency",
  "maximumPermanency",
  "type",
];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  name: "Nome",
  use: "Uso",
  minimumVelocity: "Velocidade Mínima",
  maxVelocity: "Velocidade Máxima",
  minimumPermanency: "Permanência Mínima",
  maximumPermanency: "Permanência Máxima",
  type: "Tipo",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 260,
    defaultWidth: 260,
    maxWidth: 260,
  },
  // origin: {
  //   width: 200,
  //   defaultWidth: 200,
  //   maxWidth: 200,
  // },
  // destination: {
  //   width: 200,
  //   defaultWidth: 200,
  //   maxWidth: 200,
  // },
  // vehicle: {
  //   width: 260,
  //   defaultWidth: 260,
  //   maxWidth: 260,
  // },
};

const getSortDir = (
  sortDir: SortDirectionType,
  sortBy: string,
  previousSortBy: string
) => {
  if (sortBy === previousSortBy) {
    return sortDir === SortDirection.ASCENDING
      ? SortDirection.DESCENDING
      : SortDirection.ASCENDING;
  }
  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  pages: number;
  areas: CreateAreaReworkPayload[];
  searchValue: string;
  page: number;
  pageSize: number;
  handleSearchValueChange: (text: string) => void;
  handleDownload: () => void;
  handleCreateArea: () => void;
  handleOpenArea: (id: string) => void;
  handlePage: (params: { page: number, pages?: number }) => void;
  handleSort: (dir: string, newSort: string) => void;
  handleFilter?: () => void;
  handleDelete: (id?: string) => void;
  isLoading: boolean;
};

export const formatScope = (scope: string) => {
  const normalized = scope.toUpperCase();

  if (normalized === "GLOBAL") return "Todas as rotas";
  if (normalized === "OPCIONAL" || normalized === "OPTIONAL") return "Específicas";

  return scope;
};

const TableAreas = (props: Props) => {
  const { viewType: externalViewType } = props;
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<SortDirectionType>(
    SortDirection.ASCENDING
  );
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(
    externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const handleToggleTableSettingsDialog = () =>
    setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) =>
    setViewType(newViewType);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  const handleColumnDetailsChange = (
    column: string,
    newColumnDetails: ColumnDetails
  ) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      const newSortDir = getSortDir(sortDir, newSortBy, sortBy);
      handleCardSortChange(newSortBy, newSortDir);
      props.handleSort(newSortDir, newSortBy);
    }
  };

  const handleCardSortChange = (
    newSortBy: string,
    newSortDir: SortDirectionType
  ) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  };

  const icons = {
    id: "",
    name: "rioglyph rioglyph rioglyph-layer margin-right-10 text-color-gray text-size-20",
    use:
      "rioglyph rioglyph-global margin-right-10 text-color-gray text-size-16",
    maxVelocity:
      "rioglyph rioglyph rioglyph-speed margin-right-10 text-color-gray text-size-16",
    minimumVelocity:
      "rioglyph rioglyph rioglyph-speed margin-right-10 text-color-gray text-size-16",
    minimumPermanency:
      "rioglyph rioglyph rioglyph-time margin-right-10 text-color-gray text-size-16",
    maximumPermanency:
      "rioglyph rioglyph rioglyph-time margin-right-10 text-color-gray text-size-16",
    type: "",
  };

  const renderTableHead = (
    column: string,
    label: string,
    sortByColumn: string,
    sortDirection: SortDirectionType
  ) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : (
            <SortArrows />
          )}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = props.areas.map((area) => ({
    ...omit(hiddenColumns, area),
  })) as ReworkArea[];

  const searchResult = filteredRows;

  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD &&
    "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS &&
    "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  const renderItems = (id?: string) => {
    const items = [
      {
        value: (
          <div onClick={() => id && props?.handleOpenArea(id)}>
            <span className="rioglyph rioglyph-map-location margin-right-10 text-color-primary" />
            <span>Abrir área no mapa</span>
          </div>
        ),
      },
      {
        value: (
          <div onClick={() => props?.handleDelete(id)}>
            <span className="rioglyph rioglyph-trash margin-right-10 text-color-primary" />
            <span>Excluir</span>
          </div>
        ),
      },
    ];
    return items;
  };

  return (
    <div id="table-areas">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column">
              <div className="btn-toolbar table-btn-toolbar">
                <Button
                  bsStyle={Button.PRIMARY}
                  iconName="rioglyph-plus"
                  onClick={props.handleCreateArea}
                >
                  Criar área
                </Button>
                <TableSearch
                  className="width-400"
                  value={props.searchValue}
                  onChange={props.handleSearchValueChange}
                  placeholder="Buscar por nome"
                />
              </div>
            </div>
          </div>
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <TableViewToggles
                initialViewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent="Table view"
                singleCardViewTooltipContent="List view"
                multiCardsViewTooltipContent="Cards view"
              />
            </div>
          </div>
          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title="Table settings"
              onHide={handleToggleTableSettingsDialog}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={defaultColumnOrder}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={columnLabels}
              disabledColumns={[]}
              closeButtonText="Close"
              resetButtonText="Reset to default"
              searchPlaceholder="Search by column name"
              notFoundMessage="No column found for this search value"
              columnsDetails={columnsDetails}
              autoLabel="Auto"
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={(val: string) => console.log(val)}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        {isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState
            headline="Sem resultados"
            message="Por favor refaça a busca."
          />
        )}
        <table className={tableClassNames}>
          <colgroup>
            {columns.map((column) =>
              renderTableCaption(column, columnsDetails[column])
            )}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) =>
                renderTableHead(column, columnLabels[column], sortBy, sortDir)
              )}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
             {props.isLoading ? (
                <TableRowLoading cols={8} rows={10} />
             ): null}

            {!props.isLoading && isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}

            {!props.isLoading && rows.map((row: ReworkArea, index: number) => {
              return (
                <tr key={index}>
                  <td key={"Nome"} data-field={"Nome"}>
                    <div className="display-flex flex-direction-row align-items-center">
                      <div className="padding-x-15">
                        <span
                          className="text-bold text-size-16 text-capitalize rioglyph rioglyph rioglyph-layer margin-right-10 text-color-gray">
                        </span>
                        <span className="text-bold text-size-14">
                          {row.name}
                        </span>

                      </div>
                    </div>
                  </td>
                  <td key={"Use"} data-field={"Uso"}>
                    <div className="display-flex align-items-center">
                      {row.scope === "TODAS ROTAS" ?
                        <span
                          className="rioglyph rioglyph-globe margin-right-10 text-color-gray text-size-16"></span> :
                        <span
                          className="rioglyph rioglyph-settings margin-right-10 text-color-gray text-size-16"></span>}
                      <span className="text-size-14 text-color-dark text-captalize">
                        {formatScope(row.scope)}
                      </span>
                    </div>
                  </td>
                  <td key={"MinimumVelocity"} data-field={"MinimumVelocity"}>
                    <span
                      className="rioglyph rioglyph rioglyph-speed margin-right-10 text-color-gray text-size-16"></span>
                    <span className="text-size-14 text-normal text-color-dark">{row.velocityMin}</span>
                  </td>
                  <td key={"MaxVelocity"} data-field={"MaxVelocity"}>
                    <span
                      className="rioglyph rioglyph rioglyph-speed margin-right-10 text-color-gray text-size-16"></span>
                    <span className="text-size-14 text-normal text-color-dark">{row.velocityMax}</span>
                  </td>
                  <td key={"MinimumPermanency"} data-field={"MinimumPermanency"}>
                    <span
                      className="rioglyph rioglyph rioglyph-time margin-right-10 text-color-gray text-size-16"></span>
                    <span
                      className="text-size-14 text-normal text-color-dark">{row.stayTimeMin?.toString().toLowerCase()}</span>
                  </td>
                  <td key={"MaximumPermanency"} data-field={"MaximumPermanency"}>
                    <span
                      className="rioglyph rioglyph rioglyph-time margin-right-10 text-color-gray text-size-16"></span>
                    <span
                      className="text-size-14 text-normal text-color-dark">{row.stayTimeMax?.toString().toLowerCase()}</span>
                  </td>
                  <td key={"Type"} data-field={"Type"}>
                    <ChipStatus status={row?.interestType} />
                  </td>
                  <td className="table-action">
                    <span>
                      <ButtonDropdown
                        bsSize="md"
                        dropdownClassName="width-200"
                        title={
                          <span className="rioglyph rioglyph-option-vertical" />
                        }
                        variant="link"
                        iconOnly
                        items={renderItems(row.id.toString())}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Paginator
        handlePage={params => props.handlePage(params)}
        page={props.page}
        pages={props.pages}
        pageSize={props.pageSize}
        disabled={rows.length == 0}
      />
    </div>
  );
};

export default TableAreas;